import { Search } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useInfiniteList from '../../../hooks/use-infinite-list';
import { useUser } from '../../../hooks/use-user';
import { CourseListItemUser } from '../../../lib/interfaces/courses';
import CourseCard, { CourseCardSkeleton } from '../components/course-card/course-card';
import { USER_JOB_LABELS } from '../constants/user-job-labels';

const PAGE_SIZE = 12;

const LearningSuitePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const buildUrl = useCallback((page: number) => `courses/user?page=${page}&limit=${PAGE_SIZE}`, []);
  const { items: courses, isLoading, isLoadingMore, hasMore, sentinelRef } = useInfiniteList<CourseListItemUser>({ buildUrl });

  const handleOpen = (course: CourseListItemUser) => {
    navigate(`/learning-suite/course/${course._id}`);
  };

  const roleLabel =
    user && user.userJobs && Array.isArray(user.userJobs)
      ? user.userJobs.map((j) => USER_JOB_LABELS[j as keyof typeof USER_JOB_LABELS] ?? j).join(' · ')
      : null;

  return (
    <>
      <div className="flex justify-between items-start gap-5">
        <div>
          <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">Learning Suite</h1>
          <p className="text-[16px] text-[#888]">{roleLabel}</p>
        </div>
        <div className="flex items-center gap-3 bg-white rounded-2xl px-3 py-2 shadow-sm min-w-[280px] text-[#888]">
          <Search className="w-4 h-4" />
          <input
            type="text"
            placeholder="Lektion suchen…"
            disabled
            className="bg-transparent outline-none text-[14px] flex-1 placeholder:text-[#888] cursor-not-allowed"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-10 text-center">
          <p className="text-[16px] text-[#12282A] font-bold mb-1">Aktuell sind keine Kurse für dich verfügbar</p>
          <p className="text-[14px] text-[#888]">Sobald neue Inhalte freigegeben werden, erscheinen sie hier.</p>
        </div>
      ) : (
        <>
          {/* Order comes from the admin's manual sorting — the server sends it, the grid just follows. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {courses.map((course) => (
              <CourseCard key={course._id} course={course} percentage={course.percentage} onOpen={handleOpen} />
            ))}

            {isLoadingMore && Array.from({ length: 2 }).map((_, i) => <CourseCardSkeleton key={`more-${i}`} />)}
          </div>

          {/* Sits below the grid so it enters the viewport once the user reaches the end. */}
          <div ref={sentinelRef} aria-hidden="true" />

          {!hasMore && <p className="text-center text-[13px] text-[#888] py-4">Alle Kurse geladen.</p>}
        </>
      )}
    </>
  );
};

export default LearningSuitePage;
